import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './SurveyQuestions.module.scss';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Table from '../../../components/table/Table';
import Modal from '../../../components/modal/Modal';
import TextField from '../../../components/textField/TextField';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const emptyOption = () => ({ value: '', score: '' });

const SurveyQuestions = ({ questions = [], surveyId, onRefresh }) => {
  const { showToast } = useToasts();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ text: '', options: [emptyOption()] });
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosClient.delete(`/surveys/questions/${deletingId}`);
      showToast('Question deleted successfully');
      setDeletingId(null);
      onRefresh();
    } catch (error) {
      showToast(error.message || 'Failed to delete question', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    { header: 'Question', key: 'text' },
    {
      header: 'Options',
      key: 'options',
      render: (row) =>
        row.options?.length
          ? row.options.map((o) => `${o.value} (${o.score})`).join(', ')
          : '—',
    },
    {
      header: '',
      key: 'actions',
      render: (row) => (
        <button className={styles.deleteBtn} onClick={() => setDeletingId(row.id)} type="button">
          <Icon name="trash" />
        </button>
      ),
    },
  ];

  const handleOptionChange = (index, field, value) => {
    setForm((prev) => {
      const options = [...prev.options];
      options[index] = { ...options[index], [field]: value };
      return { ...prev, options };
    });
  };

  const addOption = () => {
    setForm((prev) => ({ ...prev, options: [...prev.options, emptyOption()] }));
  };

  const removeOption = (index) => {
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.text.trim()) newErrors.text = 'Question text is required';
    form.options.forEach((opt, i) => {
      if (!opt.value.trim()) newErrors[`optValue_${i}`] = 'Required';
      if (opt.score === '' || opt.score === null) newErrors[`optScore_${i}`] = 'Required';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setForm({ text: '', options: [emptyOption()] });
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload = {
        text: form.text,
        options: form.options.map((o) => ({
          value: o.value,
          score: parseFloat(o.score),
        })),
      };
      await axiosClient.post(`/surveys/${surveyId}/questions`, payload);
      showToast('Question added successfully');
      handleClose();
      onRefresh();
    } catch (error) {
      showToast(error.message || 'Failed to add question', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Questions</h2>
        <Button
          label={
            <div className={styles.cta}>
              <Icon name="plus" />
              Add Question
            </div>
          }
          variant="primary-light"
          size="small"
          onClick={() => setIsModalOpen(true)}
        />
      </div>

      <Table
        columns={columns}
        data={questions}
        emptyMessage="No questions added yet."
      />

      {deletingId && (
        <Modal>
          <div className={styles.confirmModal}>
            <h2 className={styles.confirmTitle}>Are you sure you want to delete this question?</h2>
            <div className={styles.confirmButtons}>
              <Button
                label="Delete"
                size="small"
                variant="secondary"
                onClick={handleDelete}
                disabled={isDeleting}
              />
              <Button
                label="Cancel"
                size="small"
                variant="cancel"
                onClick={() => setDeletingId(null)}
              />
            </div>
          </div>
        </Modal>
      )}

      {isModalOpen && (
        <Modal>
          {isSaving && <LoadingOverlay />}
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Add Question</h3>

            <div className={styles.field}>
              <TextField
                label="Question Text *"
                value={form.text}
                onChange={(val) => {
                  setForm((prev) => ({ ...prev, text: val }));
                  if (errors.text) setErrors((prev) => ({ ...prev, text: undefined }));
                }}
                placeholder="Enter question text"
                error={errors.text}
              />
            </div>

            <div className={styles.optionsSection}>
              <div className={styles.optionsHeader}>
                <span className={styles.optionsLabel}>Options</span>
                <button className={styles.addOptionBtn} onClick={addOption} type="button">
                  <Icon name="plus" /> Add Option
                </button>
              </div>

              {form.options.map((opt, index) => (
                <div key={index} className={styles.optionRow}>
                  <div className={styles.optionValue}>
                    <TextField
                      label="Value"
                      value={opt.value}
                      onChange={(val) => handleOptionChange(index, 'value', val)}
                      placeholder="e.g. Good"
                      error={errors[`optValue_${index}`]}
                    />
                  </div>
                  <div className={styles.optionScore}>
                    <TextField
                      label="Score"
                      type="number"
                      value={opt.score}
                      onChange={(val) => handleOptionChange(index, 'score', val)}
                      placeholder="e.g. 4"
                      error={errors[`optScore_${index}`]}
                    />
                  </div>
                  {form.options.length > 1 && (
                    <button
                      className={styles.removeOptionBtn}
                      onClick={() => removeOption(index)}
                      type="button"
                    >
                      <Icon name="trash" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.modalActions}>
              <Button label="Cancel" variant="white" size="small" onClick={handleClose} />
              <Button label="Add Question" variant="primary" size="small" onClick={handleSubmit} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

SurveyQuestions.propTypes = {
  questions: PropTypes.array,
  surveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRefresh: PropTypes.func,
};

export default SurveyQuestions;
